import { useEffect, useRef, useState } from 'react';

type UseTypingSuggestionsOptions = {
  active: boolean;
  phrases: string[];
  holdMs?: number;
  initialDelayMs?: number;
  typingMinMs?: number;
  typingVarianceMs?: number;
  deletingMs?: number;
  pauseBetweenPhrasesMs?: number;
};

export function useTypingSuggestions({
  active,
  phrases,
  holdMs = 2200,
  initialDelayMs = 700,
  typingMinMs = 42,
  typingVarianceMs = 26,
  deletingMs = 18,
  pauseBetweenPhrasesMs = 380,
}: UseTypingSuggestionsOptions) {
  const [typedText, setTypedText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const phrasesRef = useRef(phrases);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phraseIdxRef = useRef(0);
  const charIdxRef = useRef(0);
  const isDeletingRef = useRef(false);

  useEffect(() => {
    phrasesRef.current = phrases;
  }, [phrases]);

  useEffect(() => {
    const clearLoop = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    if (!active || phrases.length === 0) {
      clearLoop();
      phraseIdxRef.current = 0;
      charIdxRef.current = 0;
      isDeletingRef.current = false;
      setTypedText('');
      return;
    }

    const tick = () => {
      const availablePhrases = phrasesRef.current;
      if (availablePhrases.length === 0) {
        clearLoop();
        setTypedText('');
        return;
      }

      if (phraseIdxRef.current >= availablePhrases.length) {
        phraseIdxRef.current = 0;
      }

      const phrase = availablePhrases[phraseIdxRef.current] || availablePhrases[0] || '';
      if (!phrase) return;

      if (!isDeletingRef.current) {
        charIdxRef.current += 1;
        setTypedText(phrase.slice(0, charIdxRef.current));

        if (charIdxRef.current >= phrase.length) {
          timeoutRef.current = setTimeout(() => {
            isDeletingRef.current = true;
            tick();
          }, holdMs);
          return;
        }

        timeoutRef.current = setTimeout(tick, typingMinMs + Math.random() * typingVarianceMs);
        return;
      }

      charIdxRef.current -= 1;
      setTypedText(phrase.slice(0, Math.max(charIdxRef.current, 0)));

      if (charIdxRef.current <= 0) {
        isDeletingRef.current = false;
        phraseIdxRef.current = (phraseIdxRef.current + 1) % availablePhrases.length;
        timeoutRef.current = setTimeout(tick, pauseBetweenPhrasesMs);
        return;
      }

      timeoutRef.current = setTimeout(tick, deletingMs);
    };

    timeoutRef.current = setTimeout(tick, initialDelayMs);
    return () => {
      clearLoop();
      phraseIdxRef.current = 0;
      charIdxRef.current = 0;
      isDeletingRef.current = false;
    };
  }, [active, holdMs, initialDelayMs, typingMinMs, typingVarianceMs, deletingMs, pauseBetweenPhrasesMs]);

  useEffect(() => {
    if (!active) return;
    const interval = setInterval(() => setShowCursor((value) => !value), 520);
    return () => clearInterval(interval);
  }, [active]);

  return { typedText, showCursor };
}
